import {
  ArrowRight,
  Award,
  BarChart3,
  Building2,
  CheckCircle,
  Database,
  Factory,
  FileText,
  GraduationCap,
  Heart,
  Landmark,
  Lock,
  Network,
  Shield,
  ShieldAlert,
  ShoppingCart,
  Target,
  TrendingUp,
  Users,
} from 'lucide-react';
import { motion } from 'motion/react';

import AnswerSection from '@src/components/seo/AnswerSection';
import { Button } from '@src/components/ui/button';
import { Card, CardContent } from '@src/components/ui/card';
import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

import { ScenarioCarousel } from './ScenarioCarousel';
import { alderwickScenario } from './scenarios/alderwick';
import { godwitScenario } from './scenarios/godwit';
import { halcyonScenario } from './scenarios/halcyon';
import { havenridgeScenario } from './scenarios/havenridge';
import { kestrelScenario } from './scenarios/kestrel';
import { nordvikScenario } from './scenarios/nordvik';
import { stonewatchScenario } from './scenarios/stonewatch';

// Industry data
const industriesData = {
  financial: {
    id: 'financial',
    name: 'Financial Services',
    tagline: 'Secure Your Financial Operations',
    heroTitle: 'Cybersecurity Built for Financial Services',
    heroDescription: 'Meet complex regulatory requirements like SOX, PCI DSS, and GLBA while protecting sensitive financial data with BOSSS unified security platform.',
    icon: Landmark,
    color: 'from-blue-500 to-blue-600',
    bgGradient: 'from-blue-900 via-blue-800 to-slate-900',
    challenges: [
      { title: 'Complex Regulatory Landscape', description: 'Navigate SOX, PCI DSS, GLBA, and state-specific regulations with automated compliance tracking.' },
      { title: 'Sensitive Data Protection', description: 'Protect customer financial data, transaction records, and PII across all systems.' },
      { title: 'Real-time Fraud Detection', description: 'Detect and respond to fraudulent activities before they impact customers.' },
      { title: 'Audit Trail Requirements', description: 'Maintain comprehensive audit logs for regulators and internal audits.' },
    ],
    solutions: [
      { title: 'Automated SOX Compliance', description: 'Pre-built workflows for SOX control testing and evidence collection', icon: FileText },
      { title: 'PCI DSS Framework', description: 'Complete PCI DSS v4.0 control mapping with continuous monitoring', icon: Shield },
      { title: 'Real-time Risk Monitoring', description: 'AI-powered risk scoring and automated alert escalation', icon: ShieldAlert },
      { title: 'Identity Governance', description: 'Automated access reviews and segregation of duties enforcement', icon: Users },
      { title: 'Comprehensive Audit Trails', description: 'Immutable logs with advanced search and reporting', icon: Database },
      { title: 'Third-Party Risk Management', description: 'Vendor assessments and ongoing monitoring', icon: Target },
    ],
    capabilities: [
      { value: 'SOX', label: 'Compliance Ready', icon: Award },
      { value: 'PCI DSS', label: 'Framework Support', icon: Shield },
      { value: 'GLBA', label: 'Privacy Controls', icon: Lock },
      { value: 'FFIEC', label: 'Guidelines Mapped', icon: FileText },
    ],
    valueProps: {
      title: 'Why BOSSS for Financial Services',
      description: 'Purpose-built to address the unique compliance and security challenges facing financial institutions.',
      benefits: ['Automated evidence collection for SOX audits', 'Pre-mapped PCI DSS v4.0 controls', 'Real-time compliance monitoring'],
    },
    frameworks: ['SOX', 'PCI DSS v4.0', 'GLBA', 'FFIEC', 'NYDFS', 'CCPA'],
    scenario: halcyonScenario,
  },
  healthcare: {
    id: 'healthcare',
    name: 'Healthcare',
    tagline: 'Protect Patient Data with Confidence',
    heroTitle: 'HIPAA-Ready Security for Healthcare',
    heroDescription: 'Safeguard protected health information (PHI) while maintaining operational efficiency with BOSSS comprehensive healthcare security platform.',
    icon: Heart,
    color: 'from-red-500 to-red-600',
    bgGradient: 'from-red-900 via-red-800 to-slate-900',
    challenges: [
      { title: 'HIPAA Compliance Complexity', description: 'Navigate the Security Rule, Privacy Rule, and Breach Notification requirements.' },
      { title: 'Patient Data Security', description: 'Protect PHI across EHRs, medical devices, and third-party systems.' },
      { title: 'Medical Device Management', description: 'Secure IoT devices, imaging equipment, and connected medical devices.' },
      { title: 'Third-Party Risk Assessment', description: 'Manage BAA requirements and vendor security assessments.' },
    ],
    solutions: [
      { title: 'HIPAA Security Framework', description: 'Complete HIPAA Security Rule control mapping and monitoring', icon: Shield },
      { title: 'PHI Access Controls', description: 'Role-based access with continuous monitoring and auditing', icon: Lock },
      { title: 'Medical Device Inventory', description: 'Automated discovery and security assessment of medical devices', icon: Database },
      { title: 'Vendor Risk Management', description: 'BAA tracking and ongoing vendor security monitoring', icon: Target },
      { title: 'Breach Notification Automation', description: 'Automated breach detection and notification workflows', icon: ShieldAlert },
      { title: 'Security Awareness Training', description: 'Healthcare-specific phishing simulations and training', icon: Users },
    ],
    capabilities: [
      { value: 'HIPAA', label: 'Compliance Ready', icon: Award },
      { value: 'HITRUST', label: 'Framework Support', icon: Shield },
      { value: 'SOC 2', label: 'Audit Ready', icon: FileText },
      { value: 'PHI', label: 'Data Protection', icon: Lock },
    ],
    valueProps: {
      title: 'Why BOSSS for Healthcare',
      description: 'Designed to protect patient data while streamlining HIPAA compliance across your organization.',
      benefits: ['HIPAA Security Rule control mapping', 'Automated BAA vendor tracking', 'PHI access monitoring and auditing'],
    },
    frameworks: ['HIPAA', 'HITRUST', 'NIST CSF', 'SOC 2', 'FDA CFR Part 11'],
    scenario: kestrelScenario,
  },
  education: {
    id: 'education',
    name: 'Education',
    tagline: 'Secure Learning Environments',
    heroTitle: 'Cybersecurity for Educational Institutions',
    heroDescription: 'Protect student records, research data, and institutional systems with BOSSS cost-effective security solutions designed for education.',
    icon: GraduationCap,
    color: 'from-green-500 to-green-600',
    bgGradient: 'from-green-900 via-green-800 to-slate-900',
    challenges: [
      { title: 'FERPA Compliance', description: 'Protect student education records and maintain required access controls.' },
      { title: 'Limited Cybersecurity Budgets', description: 'Maximize security outcomes with constrained resources.' },
      { title: 'Diverse User Populations', description: 'Manage students, faculty, staff, and guest access securely.' },
      { title: 'Legacy System Integration', description: 'Secure aging infrastructure while modernizing operations.' },
    ],
    solutions: [
      { title: 'FERPA Compliance Automation', description: 'Automated student record protection and access monitoring', icon: FileText },
      { title: 'Cost-Effective Security', description: 'Enterprise-grade security at education-friendly pricing', icon: TrendingUp },
      { title: 'Identity Lifecycle Management', description: 'Automated provisioning for students, faculty, and staff', icon: Users },
      { title: 'Legacy System Risk Assessment', description: 'Security assessment and monitoring for legacy systems', icon: Database },
      { title: 'Incident Response Planning', description: 'Education-specific incident response playbooks', icon: ShieldAlert },
      { title: 'Research Data Protection', description: 'Secure research data and grant compliance', icon: Lock },
    ],
    capabilities: [
      { value: 'FERPA', label: 'Compliance Ready', icon: Award },
      { value: 'GLBA', label: 'Framework Support', icon: Shield },
      { value: 'NIST', label: 'Controls Mapped', icon: FileText },
      { value: 'State', label: 'Privacy Laws', icon: Lock },
    ],
    valueProps: {
      title: 'Why BOSSS for Education',
      description: 'Affordable, comprehensive security designed for the unique needs of educational institutions.',
      benefits: ['FERPA compliance automation', 'Student/faculty identity lifecycle', 'Budget-friendly pricing'],
    },
    frameworks: ['FERPA', 'GLBA', 'NIST CSF', 'PCI DSS', 'State Privacy Laws'],
    scenario: alderwickScenario,
  },
  government: {
    id: 'government',
    name: 'Government',
    tagline: 'Secure Public Sector Operations',
    heroTitle: 'FedRAMP-Ready Security for Government',
    heroDescription: 'Meet federal security requirements and protect citizen data with BOSSS government-grade cybersecurity platform.',
    icon: Building2,
    color: 'from-purple-500 to-purple-600',
    bgGradient: 'from-purple-900 via-purple-800 to-slate-900',
    challenges: [
      { title: 'FedRAMP & FISMA Requirements', description: 'Navigate complex federal security authorization requirements.' },
      { title: 'Citizen Data Protection', description: 'Safeguard sensitive citizen PII and government records.' },
      { title: 'Inter-Agency Coordination', description: 'Maintain security across multi-agency environments.' },
      { title: 'Budget Constraints', description: 'Maximize security with limited public sector budgets.' },
      { title: 'Multi-Framework Compliance', description: 'Simultaneously satisfy CJIS, HIPAA, and PCI DSS requirements across diverse agency functions.' },
    ],
    solutions: [
      { title: 'FedRAMP-Ready Controls', description: 'Pre-mapped controls for FedRAMP authorization', icon: Shield },
      { title: 'FISMA Compliance', description: 'Continuous monitoring and POA&M management', icon: FileText },
      { title: 'Citizen Privacy Protection', description: 'Privacy impact assessments and data protection', icon: Lock },
      { title: 'Multi-Agency Governance', description: 'Centralized security management across agencies', icon: Building2 },
      { title: 'Continuous Monitoring', description: '24/7 security monitoring with automated reporting', icon: ShieldAlert },
      { title: 'Zero Trust Architecture', description: 'Identity-centric security for government networks', icon: Target },
      { title: 'CJIS Security Policy Compliance', description: 'Pre-built controls and audit workflows aligned to the FBI CJIS Security Policy for law enforcement and criminal justice agencies', icon: Award },
      { title: 'HIPAA for Government Agencies', description: 'HIPAA Security and Privacy Rule compliance for agencies handling protected health information, including Medicaid, VA, and public health programs', icon: Heart },
      { title: 'PCI DSS for Government Payments', description: 'Payment card security controls and automated evidence collection for agencies processing citizen payments', icon: Database },
    ],
    capabilities: [
      { value: 'FedRAMP', label: 'Controls Ready', icon: Award },
      { value: 'FISMA', label: 'Compliance Support', icon: Shield },
      { value: 'NIST 800-53', label: 'Framework Mapped', icon: FileText },
      { value: 'Zero Trust', label: 'Architecture', icon: Lock },
      { value: 'CJIS', label: 'Law Enforcement Ready', icon: Building2 },
      { value: 'HIPAA', label: 'Health Data Compliance', icon: Heart },
      { value: 'PCI DSS', label: 'Payment Security', icon: ShieldAlert },
      { value: 'CMMC', label: 'Defense Cybersecurity', icon: Target },
    ],
    valueProps: {
      title: 'Why BOSSS for Government',
      description: 'Built with federal security requirements in mind, helping agencies achieve and maintain compliance.',
      benefits: [
        'FedRAMP control mapping',
        'FISMA continuous monitoring',
        'NIST 800-53 framework support',
        'CJIS security policy compliance for law enforcement',
        'HIPAA compliance for government healthcare programs',
        'PCI DSS controls for citizen-facing payment systems',
      ],
    },
    frameworks: ['FedRAMP', 'FISMA', 'NIST 800-53', 'NIST 800-171', 'CISA BOD', 'CJIS', 'HIPAA', 'PCI DSS'],
    scenario: nordvikScenario,
  },
  manufacturing: {
    id: 'manufacturing',
    name: 'Manufacturing',
    tagline: 'Secure Industrial Operations',
    heroTitle: 'OT/IT Security for Manufacturing',
    heroDescription: 'Bridge the gap between operational technology and IT security with BOSSS unified manufacturing security platform.',
    icon: Factory,
    color: 'from-orange-500 to-orange-600',
    bgGradient: 'from-orange-900 via-orange-800 to-slate-900',
    challenges: [
      { title: 'OT/IT Security Convergence', description: 'Unify security across operational and information technology environments.' },
      { title: 'Supply Chain Risk', description: 'Assess and monitor third-party and supply chain security risks.' },
      { title: 'Industrial Control Systems', description: 'Secure PLCs, SCADA systems, and industrial IoT devices.' },
      { title: 'Regulatory Compliance', description: 'Meet industry-specific regulations and customer security requirements.' },
    ],
    solutions: [
      { title: 'OT/IT Unified Monitoring', description: 'Single pane of glass for IT and OT security', icon: BarChart3 },
      { title: 'Supply Chain Risk Assessment', description: 'Vendor security scoring and continuous monitoring', icon: Target },
      { title: 'ICS Security Controls', description: 'Industrial control system protection and monitoring', icon: Shield },
      { title: 'Asset Discovery', description: 'Automated discovery of IT and OT assets', icon: Database },
      { title: 'Incident Response for OT', description: 'Manufacturing-specific incident playbooks', icon: ShieldAlert },
      { title: 'Compliance Management', description: 'NIST CSF, IEC 62443, and customer requirements', icon: FileText },
    ],
    capabilities: [
      { value: 'IEC 62443', label: 'OT Security', icon: Award },
      { value: 'NIST CSF', label: 'Framework Support', icon: Shield },
      { value: 'CMMC', label: 'Defense Ready', icon: FileText },
      { value: 'OT/IT', label: 'Unified View', icon: BarChart3 },
    ],
    valueProps: {
      title: 'Why BOSSS for Manufacturing',
      description: 'Unified visibility across IT and OT environments with industrial-grade security controls.',
      benefits: ['OT/IT unified security monitoring', 'ICS asset discovery', 'Supply chain risk management'],
    },
    frameworks: ['NIST CSF', 'IEC 62443', 'ISO 27001', 'CMMC', 'Customer Requirements'],
    scenario: godwitScenario,
  },
  retail: {
    id: 'retail',
    name: 'Retail',
    tagline: 'Protect Customer Trust',
    heroTitle: 'PCI DSS Compliance for Retail',
    heroDescription: 'Secure customer payment data and protect your brand reputation with BOSSS retail-focused security platform.',
    icon: ShoppingCart,
    color: 'from-pink-500 to-pink-600',
    bgGradient: 'from-pink-900 via-pink-800 to-slate-900',
    challenges: [
      { title: 'PCI DSS Compliance', description: 'Meet payment card industry data security standards across all locations.' },
      { title: 'Customer Data Protection', description: 'Safeguard customer PII, payment data, and loyalty information.' },
      { title: 'Multi-Location Security', description: 'Maintain consistent security across all stores and locations.' },
      { title: 'Seasonal Workforce', description: 'Manage access for temporary and seasonal employees securely.' },
    ],
    solutions: [
      { title: 'PCI DSS v4.0 Automation', description: 'Complete PCI compliance with automated evidence collection', icon: Shield },
      { title: 'Customer Privacy Controls', description: 'CCPA, GDPR, and state privacy law compliance', icon: Lock },
      { title: 'Multi-Site Governance', description: 'Centralized security management for all locations', icon: Building2 },
      { title: 'Temporary Access Management', description: 'Automated provisioning and deprovisioning for seasonal staff', icon: Users },
      { title: 'Point-of-Sale Security', description: 'POS system monitoring and protection', icon: ShieldAlert },
      { title: 'E-commerce Protection', description: 'Web application security and fraud detection', icon: Target },
    ],
    capabilities: [
      { value: 'PCI DSS', label: 'v4.0 Ready', icon: Award },
      { value: 'CCPA', label: 'Privacy Controls', icon: Lock },
      { value: 'GDPR', label: 'EU Compliance', icon: Shield },
      { value: 'Multi-Site', label: 'Governance', icon: Building2 },
    ],
    valueProps: {
      title: 'Why BOSSS for Retail',
      description: 'Protect customer payment data and achieve PCI DSS compliance across all your locations.',
      benefits: ['PCI DSS v4.0 automation', 'Multi-location security management', 'Seasonal workforce access control'],
    },
    frameworks: ['PCI DSS v4.0', 'CCPA', 'GDPR', 'State Privacy Laws', 'SOC 2'],
    scenario: havenridgeScenario,
  },
  mssp: {
    id: 'mssp',
    name: 'MSSP',
    tagline: 'Up Your Game with a Complete Multi-Tenant Platform',
    heroTitle: 'The Security Operations Platform for MSSPs Ready to Level Up',
    heroDescription:
      'Ready to up your game? Deliver premium managed security services at scale with 9 integrated modules, 127 pentest tools, 95 native integrations, and true multi-tenant architecture. GRC, IAM, CMDB, XDR, Pentest, Service Desk, and '
      + 'more—built for MSSPs who want to stand out.',
    icon: Network,
    color: 'from-sky-500 to-sky-600',
    bgGradient: 'from-sky-900 via-sky-800 to-slate-900',
    challenges: [
      { title: 'Multi-Tenant Management', description: 'Efficiently manage security operations for multiple clients from a single platform with complete data isolation.' },
      { title: 'Operational Scalability', description: 'Scale your service delivery without proportionally increasing headcount or overhead.' },
      { title: 'SOC Operations Efficiency', description: 'Maximize analyst productivity with unified dashboards and automated workflows across all client environments.' },
      { title: 'Diverse Compliance Requirements', description: 'Support clients across industries with varying regulatory needs—SOC 2, HIPAA, PCI DSS, and more.' },
      { title: 'Client Reporting & Visibility', description: 'Provide meaningful security metrics and compliance reports to clients with white-label branding.' },
      { title: 'Rapid Client Onboarding', description: 'Deploy comprehensive security coverage for new clients quickly with pre-built integrations.' },
    ],
    solutions: [
      { title: 'GRC - Compliance Automation', description: 'Manage multi-framework compliance for each client—SOC 2, HIPAA, PCI DSS, ISO 27001—with automated evidence collection and audit-ready reports', icon: Shield },
      { title: 'IAM - Identity Governance', description: 'Centralized access reviews, certification campaigns, and privilege management across all client environments', icon: Users },
      { title: 'CMDB - Asset Discovery', description: 'Unified asset inventory across all clients with 22+ asset categories, dependency mapping, and configuration tracking', icon: Database },
      { title: 'XDR - Threat Detection', description: 'Real-time threat detection and automated incident response with MITRE ATT&CK mapping across client environments', icon: ShieldAlert },
      { title: 'Pentest - Security Assessments', description: 'Comprehensive penetration testing — 127 tools across 76+ containers including Nmap, Metasploit, Nuclei, ZAP, BloodHound, and MobSF', icon: Target },
      { title: 'Service Desk - Ticket Management', description: 'Unified ticketing with SLA tracking, knowledge base, and automated escalations across all clients', icon: FileText },
    ],
    capabilities: [
      { value: '9', label: 'Integrated Modules', icon: Network },
      { value: '127', label: 'Pentest Tools', icon: Shield },
      { value: '76+', label: 'Pentest Containers', icon: Target },
      { value: 'True', label: 'Multi-Tenancy', icon: Building2 },
    ],
    valueProps: {
      title: 'Why MSSPs Choose BOSSS to Up Their Game',
      description: 'Stop cobbling together point solutions. BOSSS gives you the unified platform to deliver premium services—and the competitive edge to win more clients.',
      benefits: [
        'True multi-tenant architecture with complete client data isolation',
        'Unified platform: GRC, IAM, CMDB, XDR, Pentest, Service Desk, Documents, Business, BCRP',
        'Seamless entity switching for efficient multi-client management',
        'White-label client portals with real-time security dashboards',
        '127 pentest tools across 76+ containers and 42 pre-built scan types',
        '95 native integrations across SIEM, EDR, IAM, vuln scanners, threat intel, and OT/ICS',
      ],
    },
    frameworks: ['SOC 2', 'ISO 27001', 'NIST CSF', 'HIPAA', 'PCI DSS', 'CIS Controls', 'MITRE ATT&CK', 'GDPR'],
    scenario: stonewatchScenario,
    // MSSP-specific product highlights
    products: [
      {
        name: 'GRC',
        title: 'Governance, Risk & Compliance',
        description: 'Manage compliance for multiple clients with pre-built frameworks and automated evidence collection.',
        features: ['Multi-framework support (SOC 2, ISO 27001, HIPAA)', 'Automated evidence collection', 'Risk heat maps per client', 'Policy templates'],
        href: '/platform/grc',
      },
      {
        name: 'IAM',
        title: 'Identity & Access Management',
        description: 'Centralize access governance across all client environments with automated reviews.',
        features: ['Access certification campaigns', 'Privilege escalation detection', 'Role management', 'Entitlement reviews'],
        href: '/platform/iam',
      },
      {
        name: 'CMDB',
        title: 'Configuration Management',
        description: 'Unified asset inventory with 22+ categories across all client environments.',
        features: ['22+ asset categories', 'Auto-discovery integrations', 'Dependency mapping', 'Change tracking'],
        href: '/platform/cmdb',
      },
      {
        name: 'XDR',
        title: 'Extended Detection & Response',
        description: 'Real-time threat detection and automated response across client environments.',
        features: ['MITRE ATT&CK mapping', 'Automated incident response', 'IOC management', 'Threat intelligence'],
        href: '/platform/xdr',
      },
      {
        name: 'Pentest',
        title: 'Penetration Testing',
        description: 'Comprehensive security assessments — 127 tools across 76+ containers, 42 pre-built scan types for client validations.',
        features: ['Nmap, Metasploit, Nuclei', 'OWASP ZAP scanning', 'Vulnerability prioritization', 'Remediation tracking'],
        href: '/platform/pentest',
      },
      {
        name: 'Service Desk',
        title: 'IT Service Management',
        description: 'Unified ticketing and SLA management across all client environments.',
        features: ['Multi-tenant ticketing', 'SLA tracking per client', 'Knowledge base', 'Automated routing'],
        href: '/platform/service-desk',
      },
    ],
  },
};

export function IndustryDetailPage({ industryId }) {
  const onNavigateToTop = useNavigateToTop();
  const industry = industriesData[industryId];

  if (!industry) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Industry Not Found</h1>
          <Button onClick={() => onNavigateToTop('/industries')}>View All Industries</Button>
        </div>
      </div>
    );
  }

  const IconComponent = industry.icon;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className={`relative py-24 bg-gradient-to-br ${industry.bgGradient} overflow-hidden`}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            className="text-center"
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            initial={{ opacity: 0, y: 30 }}
          >
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6 border border-white/20">
              <IconComponent className="w-4 h-4 mr-2 text-white" />
              <span className="text-white">{industry.tagline}</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {industry.heroTitle}
            </h1>

            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-8">
              {industry.heroDescription}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 font-semibold"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                variant="outline"
                className="px-8 py-4 border-2 border-white/80 text-white bg-white/10 hover:bg-white/20 font-semibold backdrop-blur-sm"
                onClick={() => onNavigateToTop('/contact')}
              >
                Contact Sales
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Answer-first overview (SEO-82) */}
      <AnswerSection path={`/industries/${industryId}`} />


      {/* Capabilities Section */}
      <section className="py-16 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h3 className="text-lg font-semibold text-gray-900">
              Platform Capabilities for
              {' '}
              {industry.name}
            </h3>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {industry.capabilities.map((capability, index) => {
              const CapabilityIcon = capability.icon;
              return (
                <motion.div
                  key={capability.label}
                  className="text-center"
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br ${industry.color} flex items-center justify-center`}>
                    <CapabilityIcon className="w-6 h-6 text-white" />
                  </div>

                  <p className={`text-xl font-bold bg-gradient-to-r ${industry.color} bg-clip-text text-transparent`}>
                    {capability.value}
                  </p>

                  <p className="text-gray-600 text-sm mt-1">{capability.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {industry.name}
              {' '}
              Security Challenges
            </h2>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We understand the unique security challenges facing
              {' '}
              {industry.name.toLowerCase()}
              {' '}
              organizations.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {industry.challenges.map((challenge, index) => (
              <motion.div
                key={challenge.title}
                viewport={{ once: true }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full border-gray-200 hover:border-gray-300 transition-all">
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{challenge.title}</h3>
                    <p className="text-gray-600">{challenge.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How BOSSS Solves It
            </h2>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Purpose-built solutions for
              {' '}
              {industry.name.toLowerCase()}
              {' '}
              security and compliance.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industry.solutions.map((solution, index) => {
              const SolutionIcon = solution.icon;
              return (
                <motion.div
                  key={solution.title}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="h-full border-gray-200 hover:shadow-lg transition-all group">
                    <CardContent className="p-6">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${industry.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <SolutionIcon className="w-6 h-6 text-white" />
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2">{solution.title}</h3>
                      <p className="text-gray-600 text-sm">{solution.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Scenario Walkthrough - only for industries with a scenario */}
      {industry.scenario && <ScenarioCarousel scenario={industry.scenario} />}

      {/* Product Modules Section - only for industries with products array */}
      {industry.products && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              viewport={{ once: true }}
              className="text-center mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Complete Platform for
                {' '}
                {industry.name}
                s
              </h2>

              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Every module you need to deliver comprehensive managed security services.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {industry.products.map((product, index) => (
                <motion.div
                  key={product.name}
                  viewport={{ once: true }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card
                    className="h-full border-gray-200 hover:shadow-lg hover:border-sky-300 transition-all group cursor-pointer"
                    onClick={() => onNavigateToTop(product.href)}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className={`px-3 py-1 rounded-full bg-gradient-to-r ${industry.color} text-white text-sm font-semibold`}>
                          {product.name}
                        </span>

                        <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
                      </div>

                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">
                        {product.title}
                      </h3>

                      <p className="text-gray-600 text-sm mb-4">{product.description}</p>

                      <div className="space-y-2">
                        {product.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-500">
                            <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why BOSSS Section */}
      <section className={`py-20 bg-gradient-to-br ${industry.bgGradient}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardContent className="p-8 lg:p-12">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <p className="text-white/60 text-sm font-medium mb-2 uppercase tracking-wider">Why BOSSS</p>

                    <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                      {industry.valueProps.title}
                    </h3>

                    <p className="text-lg text-white/90 mb-6">
                      {industry.valueProps.description}
                    </p>
                  </div>

                  <div>
                    <p className="text-white font-semibold mb-4">Key Benefits:</p>

                    <ul className="space-y-3">
                      {industry.valueProps.benefits.map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-white/90">{benefit}</span>
                        </li>
                      ))}
                    </ul>

                    <Button
                      className="mt-6 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600"
                      onClick={() => onNavigateToTop('/demo')}
                    >
                      Schedule a Demo
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Frameworks Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Supported Compliance Frameworks
            </h3>

            <div className="flex flex-wrap justify-center gap-4">
              {industry.frameworks.map((framework) => (
                <span
                  key={framework}
                  className={`px-4 py-2 rounded-full bg-gradient-to-r ${industry.color} text-white text-sm font-medium`}
                >
                  {framework}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            viewport={{ once: true }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Secure Your
              {' '}
              {industry.name}
              {' '}
              Operations?
            </h2>

            <p className="text-xl text-slate-400 mb-8">
              See how BOSSS can help your organization achieve security and compliance goals.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                className="px-8 py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-gray-900 hover:from-amber-500 hover:to-amber-600 font-semibold"
                onClick={() => onNavigateToTop('/demo')}
              >
                Schedule a Demo
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <Button
                className="px-8 py-4 border-2 border-slate-500 bg-transparent text-slate-300 hover:bg-slate-700 hover:text-white font-semibold"
                onClick={() => onNavigateToTop('/industries')}
              >
                Explore Other Industries
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// Export industry IDs for routing
export const industryIds = Object.keys(industriesData);

export default IndustryDetailPage;
